<div class="container mx-auto">
  <form [formGroup]="updateProfileForm">
    <!-- Header Section with breadcrumb and actions -->
    <div
      class="flex flex-wrap items-center justify-between mb-6 pb-4 border-b border-base-300"
    >
      <div class="flex-1">
        <h2
          class="text-2xl font-bold text-base-content flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          My Profile
        </h2>
        <p class="text-sm text-base-content/60 mt-1">
          Manage your personal information and preferences
        </p>
      </div>

      @if (hasIncompleteTutorProfile) {
        <div
          class="daisy-alert daisy-alert-warning shadow-md border border-warning/30 rounded-2xl mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
        >
          <div class="flex items-center gap-3">
            <i
              class="fa-solid fa-triangle-exclamation text-xl text-amber-700"
            ></i>
            <div>
              <h4 class="font-extrabold text-amber-900 text-base">
                Set Up Your Tutor Profile
              </h4>
              <p class="text-xs text-amber-800 mt-0.5">
                Complete your instructor details, subjects, and bio to showcase
                your profile in the public tutor directory.
              </p>
            </div>
          </div>
          <a
            routerLink="/dashboard/profile/tutor-settings"
            class="daisy-btn daisy-btn-warning daisy-btn-sm gap-2 font-bold shadow-sm whitespace-nowrap"
          >
            <i class="fa-solid fa-id-card"></i>
            Set Up Tutor Profile
          </a>
        </div>
      }
      <div>
        @if (!isEditEnabled) {
          <button
            type="button"
            class="daisy-btn daisy-btn-primary gap-2 shadow-md hover:shadow-lg transition-all"
            (click)="togglePageEdit()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            Edit Profile
          </button>
        } @else {
          <div class="flex gap-2 flex-wrap">
            <button
              type="button"
              (click)="togglePageEdit(false)"
              class="daisy-btn daisy-btn-outline daisy-btn-error gap-2 hover:daisy-btn-error transition-all"
              title="Cancel editing and discard changes"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              Cancel
            </button>
            <button
              type="button"
              (click)="updateProfile()"
              class="daisy-btn daisy-btn-success gap-2 shadow-md hover:shadow-lg transition-all"
              title="Save profile changes"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Save Changes
            </button>
          </div>
        }
      </div>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <!-- Avatar Section -->
        <section class="user_avatar mb-8">
          <div class="daisy-card bg-base-100 border border-base-300">
            <div class="daisy-card-body">
              <h3 class="daisy-card-title text-lg mb-4">Profile Picture</h3>
              <div class="flex justify-center">
                <div class="avatar-wrapper">
                  <div class="avatar">
                    <black-user-avatar
                      (uploadSuccess)="avatarUploadSuccessHandler($event)"
                      [allowEdit]="isEditEnabled"
                    ></black-user-avatar>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <!-- Personal Information Section -->
        <section class="personal_info mb-8">
          <div class="daisy-card bg-base-100 border border-base-300">
            <div class="daisy-card-body">
              <h3 class="daisy-card-title text-lg mb-4">
                Personal Information
              </h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                @if (user?.profile?.personal?.firstName) {
                  <div class="daisy-form-control">
                    <label class="daisy-label">
                      <span class="daisy-label-text font-medium"
                        >First Name</span
                      >
                    </label>
                    <input
                      formControlName="firstName"
                      type="text"
                      placeholder="Enter your first name"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </div>
                }
                @if (user?.profile?.personal?.lastName) {
                  <div class="daisy-form-control">
                    <label class="daisy-label">
                      <span class="daisy-label-text font-medium"
                        >Last Name</span
                      >
                    </label>
                    <input
                      formControlName="lastName"
                      type="text"
                      placeholder="Enter your last name"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </div>
                }
                <div class="daisy-form-control">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium">Email</span>
                  </label>
                  <input
                    formControlName="email"
                    type="email"
                    placeholder="your.email@example.com"
                    class="daisy-input daisy-input-bordered w-full"
                  />
                </div>
                <div class="daisy-form-control">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium"
                      >Mobile Number</span
                    >
                  </label>
                  <input
                    formControlName="mobile"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    class="daisy-input daisy-input-bordered w-full"
                  />
                </div>
                <div class="daisy-form-control md:col-span-2">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium">Bio</span>
                    <span class="daisy-label-text-alt"
                      >Brief description about yourself</span
                    >
                  </label>
                  <black-rich-text-editor
                    formControlName="bio"
                    placeholder="Brief description about yourself..."
                    theme="snow"
                    minHeight="200px"
                  ></black-rich-text-editor>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- Address Information Section -->
      <section class="address">
        <div class="daisy-card bg-base-100 border border-base-300">
          <div class="daisy-card-body">
            <h3 class="daisy-card-title text-lg mb-4">Address Information</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="daisy-form-control md:col-span-2">
                <label class="daisy-label">
                  <span class="daisy-label-text font-medium"
                    >Address Line 1</span
                  >
                </label>
                <input
                  formControlName="addressLine1"
                  type="text"
                  placeholder="Street address, P.O. box"
                  class="daisy-input daisy-input-bordered w-full"
                />
              </div>
              <div class="daisy-form-control md:col-span-2">
                <label class="daisy-label">
                  <span class="daisy-label-text font-medium"
                    >Address Line 2</span
                  >
                  <span class="daisy-label-text-alt">Optional</span>
                </label>
                <input
                  formControlName="addressLine2"
                  type="text"
                  placeholder="Apartment, suite, unit, building, floor, etc."
                  class="daisy-input daisy-input-bordered w-full"
                />
              </div>
              <div class="daisy-form-control">
                <label class="daisy-label">
                  <span class="daisy-label-text font-medium">City</span>
                </label>
                <input
                  formControlName="city"
                  type="text"
                  placeholder="Enter city"
                  class="daisy-input daisy-input-bordered w-full"
                />
              </div>
              <div class="daisy-form-control">
                <label class="daisy-label">
                  <span class="daisy-label-text font-medium"
                    >State / Province</span
                  >
                </label>
                <input
                  formControlName="province"
                  type="text"
                  placeholder="Enter state or province"
                  class="daisy-input daisy-input-bordered w-full"
                />
              </div>
              <div class="daisy-form-control">
                <label class="daisy-label">
                  <span class="daisy-label-text font-medium">Postal Code</span>
                </label>
                <input
                  formControlName="postalCode"
                  type="text"
                  placeholder="Enter postal code"
                  class="daisy-input daisy-input-bordered w-full"
                />
              </div>
              <div class="daisy-form-control">
                <label class="daisy-label">
                  <span class="daisy-label-text font-medium">Country</span>
                </label>
                <input
                  formControlName="country"
                  type="text"
                  placeholder="Enter country"
                  class="daisy-input daisy-input-bordered w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </form>

  <black-account-deletion></black-account-deletion>
</div>
